import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { FileText, Video, Clock, CheckCircle2, AlertCircle } from 'lucide-react'
import api from '@/lib/api'
import { parseSubmissionData } from '@adthlete/common/utils/submissionDataParser'

interface SubmissionViewerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  collabId: string
  submissionId: string
}

interface SubmissionData {
  id: string
  milestoneId: string | null
  milestoneName: string
  version: number
  status: string
  contentDescription: string | Record<string, unknown>
  contentUrls: Record<string, string> | { type: string; url: string; name?: string }[] | null
  notes: string | null
  metadata: Record<string, unknown> | null
  submittedAt: string
  submitter: {
    id: string
    name: string
    avatar: string | null
  }
  reviewedBy: string | null
  reviewedAt: string | null
  reviewNotes: Record<string, string> | null
}

export const SubmissionViewerDialog: React.FC<SubmissionViewerDialogProps> = ({
  open,
  onOpenChange,
  collabId,
  submissionId,
}) => {
  const [submission, setSubmission] = useState<SubmissionData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const normalizeSubmission = (raw: any): SubmissionData => {
    return {
      id: raw?.id,
      milestoneId: raw?.milestone?.id ?? raw?.milestoneId ?? null,
      milestoneName: raw?.milestone?.name ?? raw?.milestoneName ?? 'Submission',
      version: raw?.version ?? 1,
      status: raw?.status ?? 'pending_review',
      contentDescription: raw?.contentDescription ?? '',
      contentUrls: raw?.contentUrls ?? null,
      notes: raw?.notes ?? null,
      metadata: raw?.metadata ?? null,
      submittedAt: raw?.submittedAt ?? new Date().toISOString(),
      submitter: {
        id: raw?.submitter?.id ?? '',
        name: raw?.submitter?.name ?? 'Athlete',
        avatar: raw?.submitter?.avatar ?? null,
      },
      reviewedBy: raw?.reviewedBy ?? null,
      reviewedAt: raw?.reviewedAt ?? null,
      reviewNotes: raw?.reviewNotes ?? null,
    }
  }

  const fetchSubmission = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get(
        `/marketer/collaborations/${collabId}/submissions/${submissionId}`,
      )
      setSubmission(normalizeSubmission(response.data))
    } catch (err) {
      console.error('Error fetching submission:', err)
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to load submission')
    } finally {
      setLoading(false)
    }
  }, [collabId, submissionId])

  useEffect(() => {
    if (open && collabId && submissionId) {
      fetchSubmission()
    }
  }, [open, collabId, submissionId, fetchSubmission])

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <Badge className="bg-[#C0FD72]/15 text-[#3F7A0A] border border-[#C0FD72]/30 hover:bg-[#C0FD72]/25">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        )
      case 'revision_requested':
        return (
          <Badge className="bg-orange-500/15 text-orange-600 border border-orange-500/30 hover:bg-orange-500/25">
            <AlertCircle className="w-3 h-3 mr-1" />
            Changes Requested
          </Badge>
        )
      case 'pending_review':
        return (
          <Badge className="bg-blue-500/15 text-blue-600 border border-blue-500/30 hover:bg-blue-500/25">
            <Clock className="w-3 h-3 mr-1" />
            Pending Review
          </Badge>
        )
      default:
        return (
          <Badge variant="outline" className="border-foreground/20 text-gray-700">
            {status}
          </Badge>
        )
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (seconds < 60) return 'just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`
    const months = Math.floor(days / 30)
    return `${months} month${months > 1 ? 's' : ''} ago`
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#0f1117] border border-foreground/10 text-foreground">
        <DialogHeader>
          <DialogTitle>Submission Details</DialogTitle>
          <DialogDescription>
            {loading
              ? 'Loading submission...'
              : error
                ? 'Error loading submission'
                : `Version ${submission?.version} of ${submission?.milestoneName}`}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C0FD72]"></div>
          </div>
        )}

        {error && (
          <div className="p-4 border border-red-500/30 bg-red-500/10 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {!loading && !error && submission && (
          <div className="space-y-6">
            {/* Header Info */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarImage src={submission.submitter?.avatar || undefined} />
                  <AvatarFallback>
                    {submission.submitter?.name
                      ? submission.submitter.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                      : 'AT'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-foreground">
                    {submission.submitter?.name || 'Athlete'}
                  </p>
                  <p className="text-sm text-gray-600">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {formatTimeAgo(submission.submittedAt)}
                  </p>
                </div>
              </div>
              {getStatusBadge(submission.status)}
            </div>

            <Separator />

            {/* Structured description fields (e.g. licensing codes) */}
            {(() => {
              if (!submission.contentDescription) return null
              const parsed = parseSubmissionData(submission.contentDescription)
              if (parsed.fields.length === 0) return null
              return (
                <Card className="bg-card border border-foreground/10 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base text-foreground">
                      <FileText className="h-4 w-4 text-gray-600" />
                      Submission Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="space-y-3">
                      {parsed.fields.map((field) => (
                        <div key={field.order}>
                          <dt className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                            {field.label}
                          </dt>
                          <dd className="mt-1 text-sm text-gray-800 whitespace-pre-wrap">
                            {field.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </CardContent>
                </Card>
              )
            })()}

            {/* Video — per-deliverable */}
            {(() => {
              const urls = submission.contentUrls as any
              const entries = urls?.deliverables
                ? (Object.entries(urls.deliverables) as [string, any][])
                : []
              return entries.map(([, entry], i) => {
                if (!entry || entry.sameAsDeliverableId || !entry.video) return null
                return (
                  <Card
                    key={i}
                    className="bg-card border border-foreground/10 shadow-[0_1px_2px_rgba(30,41,59,0.04)]"
                  >
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-base text-foreground">
                        <Video className="h-4 w-4 text-gray-600" />
                        Deliverable #{i + 1}
                        {entry.caption && (
                          <span className="text-xs font-normal text-gray-600">· has caption</span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="overflow-hidden rounded-xl border border-foreground/10 bg-black">
                        <video controls className="w-full" src={entry.video}>
                          Your browser does not support video playback.
                        </video>
                      </div>
                      {entry.caption && (
                        <p className="text-sm whitespace-pre-wrap text-gray-800">
                          {entry.caption}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )
              })
            })()}

            {/* Review Feedback */}
            {submission.reviewNotes && (
              <Card className="bg-card border border-foreground/10 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base text-foreground">Review Feedback</CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const rn = submission.reviewNotes as Record<string, any>
                    const entries = rn?.deliverables
                      ? (Object.entries(rn.deliverables) as [string, any][])
                      : []
                    if (entries.length === 0) {
                      return <p className="text-sm text-gray-600">No feedback provided</p>
                    }
                    return (
                      <div className="space-y-4">
                        {entries.map(([, dr], i) => {
                          if (!dr) return null
                          const components = [
                            {
                              key: 'video',
                              label: 'Video',
                              icon: <Video className="h-3.5 w-3.5 text-gray-600" />,
                            },
                            {
                              key: 'caption',
                              label: 'Caption',
                              icon: <FileText className="h-3.5 w-3.5 text-gray-600" />,
                            },
                          ].filter(({ key }) => dr[key] !== undefined)
                          return (
                            <div key={i} className="space-y-2">
                              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Deliverable #{i + 1}
                              </p>
                              {components.map(({ key, label, icon }) => {
                                const comp = dr[key]
                                const approved = comp?.status === 'approved'
                                return (
                                  <div key={key} className="text-sm">
                                    <div className="flex items-center gap-2 mb-1">
                                      {icon}
                                      <span className="font-medium text-gray-800">{label}</span>
                                      <Badge
                                        className={
                                          approved
                                            ? 'bg-[#C0FD72]/15 text-[#3F7A0A] border border-[#C0FD72]/30 text-xs'
                                            : 'bg-orange-500/15 text-orange-600 border border-orange-500/30 text-xs'
                                        }
                                      >
                                        {approved ? 'Approved' : 'Changes Requested'}
                                      </Badge>
                                    </div>
                                    {!approved && comp?.comment && (
                                      <p className="text-gray-600 pl-5 border-l-2 border-orange-500/40 ml-1">
                                        {comp.comment}
                                      </p>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })()}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
